import { useEffect, useRef, useState } from "react";
import { Loader2, Dices, Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, USER_ID } from "../api";
import { badgeToast, melaToast, MelaCoin } from "./BadgesSheet";

const DICE = [Dice1, Dice2, Dice3, Dice4, Dice5, Dice6];
const TOKEN_COLORS = ["#D4AF37", "#E25822", "#2E9E5B", "#4B7BE5"];
const HOWTO_KEY = "xp_howto_saap";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// cell centre in % — square 1 is bottom-left, boustrophedon upward
const cellPos = (n) => {
  const idx = n - 1;
  const rowFromBottom = Math.floor(idx / 10);
  let col = idx % 10;
  if (rowFromBottom % 2 === 1) col = 9 - col;
  return { x: (col + 0.5) * 10, y: (9 - rowFromBottom + 0.5) * 10 };
};

const gridCells = [];
for (let rt = 0; rt < 10; rt++) {
  for (let c = 0; c < 10; c++) {
    const rowFromBottom = 9 - rt;
    const col = rowFromBottom % 2 === 1 ? 9 - c : c;
    gridCells.push(rowFromBottom * 10 + col + 1);
  }
}

const BoardSvg = ({ board }) => (
  <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
    {board.ladders.map((l) => {
      const a = cellPos(l.from), b = cellPos(l.to);
      const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
      const px = (-dy / len) * 1.1, py = (dx / len) * 1.1;
      return (
        <g key={`l${l.from}`} stroke="#D4AF37" strokeWidth="0.8" opacity="0.85">
          <line x1={a.x + px} y1={a.y + py} x2={b.x + px} y2={b.y + py} />
          <line x1={a.x - px} y1={a.y - py} x2={b.x - px} y2={b.y - py} />
          <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} strokeWidth="2.4" strokeDasharray="0.9 2.4" opacity="0.9" />
        </g>
      );
    })}
    {board.snakes.map((s) => {
      const a = cellPos(s.from), b = cellPos(s.to);
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
      const cx = mx + (-dy / len) * 7, cy = my + (dx / len) * 7;
      return (
        <g key={`s${s.from}`}>
          <path d={`M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`} fill="none" stroke="#C0392B" strokeWidth="1.5" opacity="0.85" strokeLinecap="round" />
          <circle cx={a.x} cy={a.y} r="1.6" fill="#C0392B" />
        </g>
      );
    })}
  </svg>
);

export const SaapSeedi = ({ refresh }) => {
  const [board, setBoard] = useState(null);
  const [howto, setHowto] = useState(() => localStorage.getItem(HOWTO_KEY) !== "1");
  const [phase, setPhase] = useState("setup"); // setup | playing | won
  const [count, setCount] = useState(2);
  const [names, setNames] = useState(["", "", "", ""]);
  const [positions, setPositions] = useState([]);
  const posRef = useRef([]);
  const [turn, setTurn] = useState(0);
  const [dice, setDice] = useState(null);
  const [anim, setAnim] = useState(false);
  const [busy, setBusy] = useState(false);
  const [winner, setWinner] = useState(null);
  const [reward, setReward] = useState(null);
  const session = useRef(null);
  const jumps = useRef({});

  useEffect(() => {
    api.get("/arcade/saap/board").then((r) => {
      setBoard(r.data);
      const j = {};
      r.data.ladders.forEach((l) => { j[l.from] = { to: l.to, label: l.label, kind: "ladder" }; });
      r.data.snakes.forEach((s) => { j[s.from] = { to: s.to, label: s.label, kind: "snake" }; });
      jumps.current = j;
    }).catch(() => {});
  }, []);

  const playerName = (i) => names[i].trim() || `Player ${i + 1}`;

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/arcade/session", { user_id: USER_ID, game: "saap" });
      session.current = r.data.session_id;
      posRef.current = new Array(count).fill(0);
      setPositions([...posRef.current]);
      setTurn(0);
      setDice(null);
      setWinner(null);
      setReward(null);
      setPhase("playing");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open the board"); }
    setBusy(false);
  };

  const finishOnServer = async (winnerName) => {
    try {
      const r = await api.post("/arcade/saap/finish", {
        user_id: USER_ID, session_id: session.current, players: count, winner: winnerName });
      setReward(r.data.mela_result);
      badgeToast(r.data.new_badges);
      refresh();
    } catch (e) { setReward({ error: true, message: e.response?.data?.detail || "" }); }
  };

  const roll = async () => {
    if (anim || phase !== "playing") return;
    setAnim(true);
    const v = 1 + Math.floor(Math.random() * 6);
    setDice(v);
    const fast = reduced();
    const from = posRef.current[turn];
    const target = Math.min(100, from + v);
    for (let s = from + 1; s <= target; s++) {
      posRef.current[turn] = s;
      setPositions([...posRef.current]);
      if (!fast) await sleep(160);
    }
    let final = target;
    const j = jumps.current[target];
    if (j) {
      toast(j.label, { style: { background: "#1A1208", color: j.kind === "ladder" ? "#D4AF37" : "#E25822", border: "1px solid rgba(212,175,55,0.35)" } });
      if (!fast) await sleep(500);
      final = j.to;
      posRef.current[turn] = final;
      setPositions([...posRef.current]);
    }
    if (final >= 100) {
      const wn = playerName(turn);
      setWinner(wn);
      setPhase("won");
      finishOnServer(wn);
    } else if (v !== 6) {
      setTurn((t) => (t + 1) % count);
    }
    setAnim(false);
  };

  if (!board) return <div className="rounded-2xl bg-[#121212] border border-white/10 p-5"><Loader2 size={16} className="animate-spin text-[#D4AF37]" /></div>;

  return (
    <div data-testid="game-saap-seedi" className="rounded-2xl bg-[#121212] border border-[#2E9E5B]/40 p-5">
      <p className="text-[10px] tracking-[0.3em] text-[#2E9E5B] font-bold">SAAP-SEEDI · ਸੱਪ-ਸੀੜੀ</p>
      <p className="text-xs text-white/50 mt-1">Pind-style snakes &amp; ladders — pass the tablet around the table. Winner takes {board.win_mela} Regal Coins for this passport.</p>

      {howto && (
        <div data-testid="saap-howto" className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#1A1208] p-4">
          <p className="text-[9px] tracking-[0.25em] text-[#D4AF37] font-black">HOW TO PLAY</p>
          <p className="text-xs text-white/70 mt-1.5 leading-relaxed">2-4 players take turns rolling the dice. Ladders are good pind deeds — climb up. Snakes are mela mishaps — slide down. Roll a 6 for an extra turn. First to square 100 wins!</p>
          <button data-testid="saap-howto-ok" onClick={() => { localStorage.setItem(HOWTO_KEY, "1"); setHowto(false); }}
            className="mt-3 h-9 px-5 rounded-full bg-[#D4AF37] text-black text-[10px] font-black tracking-widest">GOT IT</button>
        </div>
      )}

      {!howto && phase === "setup" && (
        <div data-testid="saap-setup" className="mt-4">
          <p className="text-[10px] tracking-widest text-white/50 font-black">PLAYERS AT THE TABLE</p>
          <div className="flex gap-2 mt-2">
            {[2, 3, 4].map((n) => (
              <button key={n} data-testid={`saap-count-${n}`} onClick={() => setCount(n)}
                className={`h-10 px-5 rounded-full border text-xs font-black ${count === n ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-white/15 text-white/50"}`}>{n}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            {Array.from({ length: count }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full shrink-0" style={{ background: TOKEN_COLORS[i] }} />
                <input data-testid={`saap-name-${i}`} value={names[i]} maxLength={10} placeholder={`Player ${i + 1}`}
                  onChange={(e) => setNames((ns) => ns.map((v, j) => (j === i ? e.target.value : v)))}
                  className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/60" />
              </div>
            ))}
          </div>
          <button data-testid="saap-start" onClick={start} disabled={busy}
            className="mt-4 w-full h-12 rounded-full bg-[#2E9E5B] text-white font-black text-sm tracking-widest disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Dices size={16} />} OPEN THE BOARD
          </button>
        </div>
      )}

      {phase !== "setup" && (
        <>
          <div className="flex items-center justify-between gap-2 mt-4 flex-wrap">
            <div className="flex gap-1.5 flex-wrap">
              {Array.from({ length: count }).map((_, i) => (
                <span key={i} data-testid={`saap-player-${i}`}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black ${i === turn && phase === "playing" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-white/50"}`}>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: TOKEN_COLORS[i] }} />
                  {playerName(i).toUpperCase()} · {positions[i] || "START"}
                </span>
              ))}
            </div>
            {dice && (
              <span data-testid="saap-dice" className="text-[#D4AF37]">
                {(() => { const D = DICE[dice - 1]; return <D size={26} />; })()}
              </span>
            )}
          </div>

          <div className="relative mt-3 max-w-[560px] mx-auto rounded-xl overflow-hidden border border-[#8a6d1f]/40">
            <div className="grid grid-cols-10">
              {gridCells.map((n) => {
                const j = jumps.current[n];
                return (
                  <div key={n} className={`aspect-square border border-black/40 relative ${j ? (j.kind === "ladder" ? "bg-[#1d2a14]" : "bg-[#2a1410]") : (n % 2 ? "bg-[#1A140B]" : "bg-[#14100A]")}`}>
                    <span className="absolute top-0.5 left-1 text-[7px] font-bold text-white/30">{n}</span>
                  </div>
                );
              })}
            </div>
            <BoardSvg board={board} />
            {positions.map((p, i) => p >= 1 && (
              <span key={i} data-testid={`saap-token-${i}`}
                className="saap-token absolute w-[4.5%] h-[4.5%] rounded-full border-2 border-black/60 shadow-lg -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${cellPos(Math.min(p, 100)).x + (i % 2 ? 1.8 : -1.8)}%`, top: `${cellPos(Math.min(p, 100)).y + (i > 1 ? 1.8 : -1.8)}%`, background: TOKEN_COLORS[i], zIndex: 5 + i }} />
            ))}
          </div>

          {phase === "playing" && (
            <button data-testid="saap-roll" onClick={roll} disabled={anim}
              className="mt-4 w-full h-12 rounded-full bg-[#D4AF37] text-black font-black text-sm tracking-widest disabled:opacity-40 flex items-center justify-center gap-2">
              <Dices size={16} /> {anim ? "MOVING…" : `${playerName(turn).toUpperCase()} — ROLL THE DICE`}
            </button>
          )}

          {phase === "won" && (
            <div data-testid="saap-won" className="mt-4 rounded-xl border border-[#D4AF37]/50 bg-[#1C160A] p-4 text-center">
              <PartyPopper size={22} className="mx-auto text-[#D4AF37]" />
              <p className="font-display text-2xl font-bold mt-1">{winner} wins!</p>
              <p className="text-xs text-white/55 mt-0.5">ਵਧਾਈਆਂ! The whole table earned it.</p>
              {reward?.message && (
                <p data-testid="saap-reward" className={`text-xs font-bold mt-1.5 flex items-center justify-center gap-1.5 ${reward.error ? "text-white/45" : "text-[#F0C742]"}`}>{!reward.error && <MelaCoin size={14} />} {reward.message}</p>
              )}
              <button data-testid="saap-again" onClick={() => setPhase("setup")}
                className="mt-3 h-10 px-6 rounded-full border border-[#2E9E5B]/70 text-[#2E9E5B] text-[10px] font-black tracking-widest">PLAY AGAIN</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
